import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {ConfirmationDialog} from '@ui/overlays/dialog/confirmation-dialog';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import React, {Fragment, useEffect} from 'react';
import {useDataTable} from './data-table-context';
import {useDialogContext} from '@ui/overlays/dialog/dialog-context';
import {errorStatusIs} from '@common/http/error-status-is';
import {useValidationSelectedRows} from '@common/datatable/requests/lins-validation-selected-rows';
import {toast} from '@ui/toast/toast';
import {message} from '@ui/i18n/message';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {Form} from '@ui/forms/form';
import {useForm} from 'react-hook-form';

export interface ValidationPayload {
  // Pour chaque lien sélectionné, la raison est stockée dans un objet clé-valeur
  reason: string;
}

function AcceptItemsDialog() {
  const acceptSelectedRows = useValidationSelectedRows('accept');
  const {selectedRows, setSelectedRows} = useDataTable();

  const handleClick = () => {
    // Pour l'acceptation, nous n'avons pas besoin de raison, on envoie une chaîne vide.
    acceptSelectedRows.mutate('', {
      onError: err => {
        if (errorStatusIs(err, 422)) {
          setSelectedRows([]);
        }
      },
    });
  };

  return (
    <Button
      variant="flat"
      color="positive"
      onClick={handleClick}
      disabled={acceptSelectedRows.isPending}
    >
      <Trans message="Accept" />
    </Button>
  );
}

function DenyItemsDialog() {
  const denySelectedRows = useValidationSelectedRows('deny');
  const {selectedRows, setSelectedRows} = useDataTable();
  const form = useForm<ValidationPayload>({
    defaultValues: {
      reason: '',
    },
  });
  const {formId, close} = useDialogContext();

  // Initialise les valeurs par défaut pour chaque champ reason en fonction de selectedRows
  useEffect(() => {
    const defaults: Record<string, string> = {};
    // Réinitialise le formulaire avec les valeurs par défaut calculées
    form.reset({reason: ''});
  }, [selectedRows, form]);

  return (
    <ConfirmationDialog
      isLoading={denySelectedRows.isPending}
      title={
        <Trans
          message="Deny [one 1 item|other :count items]?"
          values={{count: selectedRows.length}}
        />
      }
      body={
        <div>
          <Trans message="This will permanently remove the items and cannot be undone." />
          <Form id={formId} form={form} onSubmit={() => {}}>
            <FormTextField
              inputElementType="textarea"
              name={`reason`}
              label={<Trans message={`Reason`} />}
              className="mb-20"
              required
              autoFocus={true}
            />
          </Form>
        </div>
      }
      confirm={<Trans message="Deny" />}
      isDanger
      onConfirm={form.handleSubmit(data => {
        // Vérifie que pour chaque lien sélectionné, une raison est fournie
        if (!data.reason) {
          toast(message('Please provide a reason'), {
            type: 'danger',
          });
          return;
        }
        // On transmet l'objet data.reason (Record<string, string>) en argument à la mutation
        denySelectedRows.mutate(data.reason, {
          onSuccess: () => close(),
          onError: err => {
            if (errorStatusIs(err, 422)) {
              setSelectedRows([]);
              close();
            }
          },
        });
      })}
    />
  );
}

export function ValidationSelectedItemsAction() {
  return (
    <Fragment>
      <DialogTrigger type="modal">
        <Button variant="flat" color="danger">
          <Trans message="Deny" />
        </Button>
        <DenyItemsDialog />
      </DialogTrigger>
      <AcceptItemsDialog />
    </Fragment>
  );
}
